import { useEffect, useState } from "react";
import { ArrowIcon, CornerMarks, EASE, MediaSlot, useFade, useKnockoutLogo } from "../about/ui";

export { ArrowIcon, CornerMarks, EASE, MediaSlot, useFade, useKnockoutLogo };

const artCache = new Map();
const artInflight = new Map();

export function useKnockoutArt(src) {
  const [href, setHref] = useState(src ? artCache.get(src) || "" : "");

  useEffect(() => {
    if (!src) {
      setHref("");
      return undefined;
    }
    if (artCache.has(src)) {
      setHref(artCache.get(src));
      return undefined;
    }

    let cancelled = false;

    const finish = (url) => {
      if (!cancelled) setHref(url);
    };

    if (artInflight.has(src)) {
      artInflight.get(src).then(finish);
      return () => {
        cancelled = true;
      };
    }

    const job = new Promise((resolve) => {
      const image = new Image();
      image.decoding = "async";
      image.onload = () => {
        try {
          resolve(punchBlackBackground(image));
        } catch {
          resolve(src);
        }
      };
      image.onerror = () => resolve(src);
      image.src = src;
    }).then((url) => {
      artCache.set(src, url);
      artInflight.delete(src);
      return url;
    });

    artInflight.set(src, job);
    job.then(finish);

    return () => {
      cancelled = true;
    };
  }, [src]);

  return href;
}

function punchBlackBackground(image) {
  const canvas = document.createElement("canvas");
  const scale = Math.min(1, 1400 / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return image.src;

  ctx.drawImage(image, 0, 0, width, height);
  const frame = ctx.getImageData(0, 0, width, height);
  const { data } = frame;
  const isBackdrop = (offset) => {
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];
    const max = Math.max(r, g, b);
    return max < 36 && max - Math.min(r, g, b) < 18;
  };

  const seen = new Uint8Array(width * height);
  const stack = [];
  const enqueue = (x, y) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return;
    const index = y * width + x;
    if (seen[index]) return;
    if (!isBackdrop(index * 4)) return;
    seen[index] = 1;
    stack.push(index);
  };

  for (let x = 0; x < width; x += 1) {
    enqueue(x, 0);
    enqueue(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    enqueue(0, y);
    enqueue(width - 1, y);
  }

  while (stack.length) {
    const index = stack.pop();
    const x = index % width;
    const y = (index - x) / width;
    data[index * 4 + 3] = 0;
    enqueue(x + 1, y);
    enqueue(x - 1, y);
    enqueue(x, y + 1);
    enqueue(x, y - 1);
  }

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x;
      if (seen[index] || data[index * 4 + 3] === 0) continue;
      const touchesClear =
        data[((y - 1) * width + x) * 4 + 3] === 0 ||
        data[((y + 1) * width + x) * 4 + 3] === 0 ||
        data[(index - 1) * 4 + 3] === 0 ||
        data[(index + 1) * 4 + 3] === 0;
      if (!touchesClear) continue;
      const offset = index * 4;
      const max = Math.max(data[offset], data[offset + 1], data[offset + 2]);
      if (max < 58) data[offset + 3] = Math.round((max / 58) * data[offset + 3]);
    }
  }

  ctx.putImageData(frame, 0, 0);
  return canvas.toDataURL("image/png");
}

export function SectionShell({ id, labelledBy, tone = "white", children }) {
  const tones = {
    white: "bg-white text-navy",
    mist: "bg-[#f3f7f5] text-navy",
    green: "bg-market-green text-white",
    navy: "bg-navy text-white",
  };

  return (
    <section
      id={id}
      data-tone={tone}
      className={`relative scroll-mt-32 overflow-hidden ${tones[tone] || tones.white}`}
      aria-labelledby={labelledBy}
    >
      <SectionWash tone={tone} />
      {children}
    </section>
  );
}

export function SectionWash({ tone = "white" }) {
  const gold = "rgba(229,184,11,0.14)";
  const green = "rgba(52,132,110,0.12)";
  const dark = tone === "green" || tone === "navy";

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{
          background: dark
            ? `radial-gradient(70% 50% at 0% 0%, ${gold}, transparent 55%), radial-gradient(55% 40% at 100% 100%, rgba(255,255,255,0.08), transparent 50%)`
            : `radial-gradient(70% 50% at 0% 0%, ${gold}, transparent 55%), radial-gradient(55% 40% at 100% 8%, ${green}, transparent 50%)`,
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          opacity: dark ? 0.22 : 0.32,
          backgroundImage: dark
            ? "radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)"
            : "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
    </div>
  );
}

export function BrandMark({ src, alt, className }) {
  const { href, failed } = useKnockoutLogo(src);
  const display = !failed && href ? href : src;

  if (!display) {
    return alt ? <span className="text-xs font-semibold tracking-tight text-navy">{alt}</span> : null;
  }

  return <img src={display} alt={alt} className={`object-contain object-center ${className}`} />;
}

export function Field({ label, name, value, onChange, type = "text", required = false, autoComplete }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40"
      >
        {label}
        {required ? <span className="text-market-gold"> *</span> : null}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        className="mt-2 min-h-12 w-full border border-navy/15 bg-[#f3f7f5] px-4 text-sm text-navy outline-none transition placeholder:text-navy/30 focus:border-market-gold focus:ring-2 focus:ring-market-gold/40"
      />
    </div>
  );
}
